import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Output, ViewEncapsulation } from '@angular/core';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridToolbarButton, GridOptions, GridRow, GridRowButton, GridRowButtonDisabledParams, RowDataChangeEvent } from '@app/core/components/grid/grid.types';
import { ModalService } from '@app/core/services/modal.service';
import { RememberService } from '@app/core/services/remember.service';
import { StateService } from '@app/core/services/state.service';
import { UtilityService } from '@app/core/services/utility.service';
import { PurchHeadGet, PurchHeadService } from '@app/services/purch-head.service';

@Component({
    selector: 'tt-purch-head-purch-lines',
    templateUrl: './purch-head-purch-lines.component.html',
    styleUrls: ['./purch-head-purch-lines.component.css'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PurchHeadPurchLinesComponent implements AfterViewInit {
    private get?: PurchHeadGet;

    public grid?: GridOptions<PurchLineItem>;

    @Output()
    public gridReady = new EventEmitter<GridOptions<PurchLineItem>>();

    private readGrid() {
        if (!this.grid) {
            this.grid = new Grid<PurchLineItem>()
                .setRememberId('w_purchhead.purchline_grid')
                .setLoadDataFunction({ method: 3805, parameters: () => ({ purch_internal_no: this.get?.purch_internal_no }) })
                .addToolbarButton(this.buildNewPurchLineButton())
                .addToolbarButton(this.buildCreateBatchDeliveryButton())
                .addRowButton(this.buildEditProductConfigRowButton());
        } else {
            this.grid.gridfunc?.read();
        }
    }

    public onGridReady() {
        this.gridReady.emit(this.grid);
        this.cdr.markForCheck();
    }

    constructor(
        private purchHead: PurchHeadService,
        private remember: RememberService,
        private modal: ModalService,
        private state: StateService,
        private utility: UtilityService,
        public cdr: ChangeDetectorRef,
    ) {
        this.purchHead.currentPurchHead$.subscribe({
            next: (get) => {
                if (!!get) {
                    this.get = get;
                    this.readGrid();
                } else {
                    delete this.get;
                    delete this.grid;
                }

                this.cdr.markForCheck();
            },
        });
    }

    public onRowChanged(event: RowDataChangeEvent) {
        if (['prod_id', 'prod_name'].includes(event.colId) && !!event.row['prod_id'] && !event.row['prodchoice_keyno'] && event.saved === true) {
            this.openProductsConfigModalForRow(event.row);
        }
    }

    private async openProductsConfigModalForRow(row: GridRow) {
        await this.remember.remember('productsconfig_selected', btoa(JSON.stringify([{ item_id: row['prod_id'], item_name: row['prod_name'], quantity: 1, editable: false }])));
        const submitted = await this.modal.openStateDialog({ state: 'productsconfig', params: `purchline/${this.get?.purch_internal_no}/${row['purchline_keyno']}` });

        if (!!submitted) {
            this.grid?.gridfunc?.read();
        }
    }

    private buildNewPurchLineButton(): GridToolbarButton {
        //TODO: modal and read grid after
        return {
            name: 'new_line',
            text: 'purchhead_purchline_new',
            translate: true,
            func: (event: MouseEvent) => {
                if (this.utility.isModifierKeyPressed(event)) {
                    this.state.newTab('purchline', { purch_internal_no: '' + this.get?.purch_internal_no, purchline_keyno: '0' });
                } else {
                    this.state.go('purchline', { purch_internal_no: '' + this.get?.purch_internal_no, purchline_keyno: '0' });
                }
            },
            icon: 'far fa-plus',
            cssClass: 'tt-button tt-button--primary im-grid-btn-xs-l',
        };
    }

    private buildCreateBatchDeliveryButton(): GridToolbarButton {
        return {
            name: 'batch',
            text: 'create_batchdelivery',
            func: async () => {
                // TODO:: to ts
                // const data = (await purchHeadService.createBatchDelivery({ businessco_no: vm.model.edit.businessco_no, stockplace_id: vm.model.edit.stokcplace_id, stocklocation_keyno: vm.model.edit.stocklocation_keyno ?? '0' }))[0];
                // popupService.openPopup(`batchdelivery_select_purchhead/${vm.model.edit.supplier_no}/${data.parms}`, { remmemberId: 'purchhead.batchdeliveryselect.popup' });
            },
            icon: 'far fa-sync-alt',
            cssClass: 'tt-button tt-button--primary im-grid-btn-xs-l',
            spinOnAction: true,
            translate: true,
            disabled: () => (this.get?.show_create_bachdelivery_button === '1' ? false : 'hidden'),
        };
    }

    private buildEditProductConfigRowButton(): GridRowButton {
        return {
            name: 'edit_product_config',
            icon: 'fas fa-cogs',
            tooltip: 'edit_product_config',
            translateTooltip: true,
            func: (row) => this.openProductsConfigModalForRow(row),
            disabled: (row: PurchLineItem, params: GridRowButtonDisabledParams) => {
                if ([0, '0', false, 'false'].includes(row.show_productsconfig_button)) {
                    return 'hidden';
                }

                return false;
            },
        };
    }

    ngAfterViewInit(): void {
        this.cdr.markForCheck();
    }
}

export interface PurchLineItem {
    purchline_keyno: number;
    is_selected: boolean;
    purch_internal_no: number;
    supplier_no: number;
    prod_id: string;
    prod_name: string;
    prodsize_keyno: number;
    prodsize_name: string;
    prodchoice_keyno: number;
    prodchoice_string: string;
    suppliers_prodno: string;
    suppliers_prodname: string;
    note_before: string;
    purchline_note: string;
    message_to_supplier: string;
    sfi: string;
    tag: string;
    order_order_no: number;
    confirmed_date: Date;
    orderby: number;
    ordered_quantity: number;
    input_price_ordval: number;
    price_ordval_4digits: number;
    price_ordval: number;
    discount_pst: number;
    amount_ordval: number;
    quantity_received_total: number;
    quantity_rest: number;
    quantity_unit_name: string;
    input_quantity: number;
    input_unit_no: number;
    input_unit_name: string;
    volume_m3: number;
    freight_locval: number;
    deliverydate_exfactory_requested: Date;
    deliverydate_exfactory_confirmed: Date;
    spesial_product: string;
    orderline_prod_id_and_name: string;
    item_state: string;
    item_parms: string;
    item_path: string;
    item_glyphicon: string;
    item_glyphicon_color: string;
    show_productsconfig_button: boolean;
}
